// src/components/PlanComparison.js
import React, { useState, useEffect } from 'react';
import {
  Paper,
  Typography,
  Box,
  Button,
  Grid,
  Card,
  CardContent,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Alert,
  Divider,
  CircularProgress
} from '@mui/material';
import {
  Check as CheckIcon,
  Close as CloseIcon,
  TrendingUp as UpgradeIcon,
  TrendingDown as DowngradeIcon,
  Star as StarIcon,
  Business as EnterpriseIcon,
  Person as BasicIcon,
  Groups as ProIcon
} from '@mui/icons-material';
import useSubscription from '../hooks/useSubscription';
import BillingPeriodToggle from './BillingPeriodToggle';

// Get correct API base URL (same logic as api/client.js)
const getApiBaseUrl = () => {
  if (process.env.NODE_ENV === 'development') {
    return process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
  } else {
    // Use proxy approach for better security - Vercel forwards /api/* to backend
    return '/api';
  }
};

const PLAN_ICONS = {
  free: <BasicIcon />,
  basic: <BasicIcon />,
  pro: <ProIcon />,
  enterprise: <EnterpriseIcon />,
  internal: <StarIcon />
};

const PLAN_COLORS = {
  free: 'default',
  basic: 'primary',
  pro: 'secondary', 
  enterprise: 'error',
  internal: 'warning'
};

const PlanComparison = ({ onUpgrade, onDowngrade, onNotification, isResubscription = false }) => {
  const { subscription, loading, error } = useSubscription();
  const [plans, setPlans] = useState([]);
  const [loadingPlans, setLoadingPlans] = useState(true);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [billingPeriod, setBillingPeriod] = useState('monthly');

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      const response = await fetch(`${getApiBaseUrl()}/billing/subscription-plans`);
      if (!response.ok) throw new Error('Failed to fetch plans');
      const data = await response.json();
      
      // Convert plans object to array, filter out internal plans and sort by tier level
      const plansArray = Object.values(data.plans);
      const publicPlans = plansArray.filter(plan => 
        plan.name !== 'internal' && plan.is_active
      ).sort((a, b) => a.tier_level - b.tier_level);
      
      setPlans(publicPlans);
    } catch (error) {
      console.error('Error fetching plans:', error);
      onNotification({
        message: 'Failed to load subscription plans',
        severity: 'error'
      });
    } finally {
      setLoadingPlans(false);
    }
  };

  const getCurrentPlan = () => {
    if (!subscription || !plans.length) return null;
    
    // Try multiple ways to match the current plan
    const matchedPlan = plans.find(plan => 
      plan.name === subscription.subscription_tier ||
      plan.name === subscription.plan_name?.toLowerCase() ||
      plan.display_name === subscription.plan_name ||
      plan.display_name === subscription.subscription_tier
    );
    
    // If no plan matched (e.g., user is on "internal" plan), create a mock current plan
    // so we can still allow upgrades to public plans
    if (!matchedPlan && subscription.subscription_tier) {
      return {
        name: subscription.subscription_tier,
        display_name: subscription.plan_name || subscription.subscription_tier,
        monthly_price: "0",
        tier_level: 0, // Assume lowest tier so all public plans are upgrades
        features: subscription.features || [],
        limits: subscription.limits || {}
      };
    }
    
    return matchedPlan;
  };

  const parseFeatures = (featuresJson) => {
    try {
      const features = typeof featuresJson === 'string' 
        ? JSON.parse(featuresJson) 
        : featuresJson;
      return Array.isArray(features) ? features : [];
    } catch {
      return [];
    }
  };

  const parseLimits = (limitsJson) => {
    try {
      const limits = typeof limitsJson === 'string' 
        ? JSON.parse(limitsJson) 
        : limitsJson;
      return limits || {};
    } catch {
      return {};
    }
  };

  const formatLimit = (value) => {
    if (value === -1 || value === null) return 'Unlimited';
    if (value >= 1000) return `${(value / 1000).toFixed(0)}k`;
    return value.toString();
  };

  const getDisplayPrice = (plan) => {
    if (billingPeriod === 'annual') {
      return {
        price: plan.annual_price || (plan.monthly_price * 10),
        period: '/year',
        monthlyEquivalent: plan.annual_price ? (plan.annual_price / 12).toFixed(2) : plan.monthly_price,
        savings: plan.monthly_price * 2 // 2 months free
      };
    }
    return {
      price: plan.monthly_price,
      period: '/month',
      monthlyEquivalent: plan.monthly_price,
      savings: 0
    };
  };

  const getPriceId = (plan) => {
    return billingPeriod === 'annual' 
      ? plan.stripe_annual_price_id || plan.stripe_price_id
      : plan.stripe_price_id;
  };

  const calculateProration = (currentPlan, targetPlan) => {
    if (!currentPlan || !targetPlan) return null;
    
    const currentPrice = parseFloat(currentPlan.monthly_price);
    const targetPrice = parseFloat(targetPlan.monthly_price);
    const priceDiff = targetPrice - currentPrice;
    
    // Simple proration calculation (assuming mid-cycle change)
    const prorated = priceDiff * 0.5; // Rough mid-cycle estimate
    
    return {
      immediate: priceDiff > 0 ? prorated : 0,
      nextCycle: targetPrice,
      savings: priceDiff < 0 ? Math.abs(priceDiff) : 0,
      isUpgrade: priceDiff > 0
    };
  };

  const handlePlanAction = (plan) => {
    const currentPlan = getCurrentPlan();
    
    // Enhanced plan object with price ID for the selected billing period
    const enhancedPlan = {
      ...plan,
      priceId: getPriceId(plan),
      billingPeriod: billingPeriod,
      displayPrice: getDisplayPrice(plan)
    };

    // For resubscription scenarios (no current plan), treat as upgrade
    if (!currentPlan || isResubscription) {
      const proration = null; // No proration for resubscription
      onUpgrade(enhancedPlan, proration);
      return;
    }

    const proration = calculateProration(currentPlan, plan);
    
    if (plan.tier_level > currentPlan.tier_level) {
      onUpgrade(enhancedPlan, proration);
    } else if (plan.tier_level < currentPlan.tier_level) {
      onDowngrade(enhancedPlan, proration);
    }
  };

  const getFeatureList = (features) => {
    const featureNames = {
      basic_clients: 'Client Management',
      basic_appointments: 'Appointment Scheduling',
      email_marketing: 'Email Marketing',
      advanced_automation: 'Advanced Automation',
      analytics: 'Analytics & Reporting',
      api_access: 'API Access',
      custom_integrations: 'Custom Integrations'
    };

    return features.map(feature => featureNames[feature] || feature);
  };

  if (loading || loadingPlans) {
    return (
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
          <Typography sx={{ ml: 2 }}>Loading plan comparison...</Typography>
        </Box>
      </Paper>
    );
  }

  if (error) {
    return (
      <Paper sx={{ p: 3, mb: 3 }}>
        <Alert severity="error">
          Failed to load subscription information: {error}
        </Alert>
      </Paper>
    );
  }

  const currentPlan = getCurrentPlan();

  return (
    <Box>
      <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <StarIcon />
        Protection Level Options
      </Typography>

      {currentPlan && (
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2">
            <strong>Current Plan:</strong> {currentPlan.display_name} 
            {currentPlan.monthly_price === "0" ? " (Internal/Free)" : ` (£${currentPlan.monthly_price}/month)`}
          </Typography>
        </Alert>
      )}

      <BillingPeriodToggle 
        value={billingPeriod} 
        onChange={setBillingPeriod}
        disabled={loadingPlans}
      />

      <Grid container spacing={3}>
        {plans.map((plan) => {
          const features = parseFeatures(plan.features);
          const limits = parseLimits(plan.limits);
          const isCurrentPlan = currentPlan?.name === plan.name;
          const proration = currentPlan ? calculateProration(currentPlan, plan) : null;
          const displayPrice = getDisplayPrice(plan);

          return (
            <Grid size={12} md={6} lg={3} key={plan.name}>
              <Card 
                variant={isCurrentPlan ? "elevation" : "outlined"}
                sx={{ 
                  height: '100%',
                  position: 'relative',
                  ...(isCurrentPlan && {
                    boxShadow: 3,
                    border: '2px solid',
                    borderColor: 'primary.main'
                  })
                }}
              >
                {isCurrentPlan && (
                  <Chip
                    label="Current Plan"
                    color="primary"
                    size="small"
                    sx={{
                      position: 'absolute',
                      top: 8,
                      right: 8,
                      zIndex: 1
                    }}
                  />
                )}
                
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    {PLAN_ICONS[plan.name]}
                    <Typography variant="h6" sx={{ ml: 1 }}>
                      {plan.display_name}
                    </Typography>
                  </Box>

                  <Typography variant="h4" color="primary" gutterBottom>
                    £{displayPrice.price}
                    <Typography variant="caption" color="text.secondary">
                      {displayPrice.period}
                    </Typography>
                  </Typography>

                  {billingPeriod === 'annual' && displayPrice.savings > 0 && (
                    <Typography variant="body2" color="success.main" sx={{ mb: 1, fontWeight: 600 }}>
                      £{displayPrice.monthlyEquivalent}/month • Save £{displayPrice.savings}
                    </Typography>
                  )}

                  {proration && !isCurrentPlan && (
                    <Alert 
                      severity={proration.isUpgrade ? "warning" : "success"} 
                      sx={{ mb: 2 }}
                      size="small"
                    >
                      <Typography variant="caption">
                        {proration.isUpgrade 
                          ? `~£${proration.immediate.toFixed(2)} prorated charge`
                          : `Save £${proration.savings.toFixed(2)}/month`
                        }
                      </Typography>
                    </Alert>
                  )}

                  <Typography variant="subtitle2" gutterBottom>
                    Limits:
                  </Typography>
                  <List dense sx={{ mb: 2 }}>
                    <ListItem disablePadding>
                      <ListItemText 
                        primary={`${formatLimit(limits.clients)} clients`}
                        primaryTypographyProps={{ variant: 'body2' }}
                      />
                    </ListItem>
                    <ListItem disablePadding>
                      <ListItemText 
                        primary={`${formatLimit(limits.automations)} automations`}
                        primaryTypographyProps={{ variant: 'body2' }}
                      />
                    </ListItem>
                    <ListItem disablePadding>
                      <ListItemText 
                        primary={`${formatLimit(limits.api_calls_per_month)} API calls/month`}
                        primaryTypographyProps={{ variant: 'body2' }}
                      />
                    </ListItem>
                  </List>

                  <Typography variant="subtitle2" gutterBottom>
                    Features:
                  </Typography>
                  <List dense sx={{ mb: 3 }}>
                    {getFeatureList(features).map((feature, index) => (
                      <ListItem key={index} disablePadding>
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          <CheckIcon color="primary" fontSize="small" />
                        </ListItemIcon>
                        <ListItemText 
                          primary={feature}
                          primaryTypographyProps={{ variant: 'body2' }}
                        />
                      </ListItem>
                    ))}
                  </List>

                  {!isCurrentPlan && currentPlan && !isResubscription && (
                    <Button
                      fullWidth
                      variant={plan.tier_level > currentPlan.tier_level ? "contained" : "outlined"}
                      color={plan.tier_level > currentPlan.tier_level ? "primary" : "warning"}
                      startIcon={
                        plan.tier_level > currentPlan.tier_level 
                          ? <UpgradeIcon /> 
                          : <DowngradeIcon />
                      }
                      onClick={() => handlePlanAction(plan)}
                    >
                      {plan.tier_level > currentPlan.tier_level ? 'Upgrade' : 'Downgrade'}
                    </Button>
                  )}

                  {(!currentPlan || isResubscription) && !isCurrentPlan && (
                    <Button
                      fullWidth
                      variant="contained"
                      color="primary"
                      onClick={() => handlePlanAction(plan)}
                    >
                      {isResubscription ? 'Resubscribe to This Plan' : 'Select Plan'}
                    </Button>
                  )}
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {currentPlan && !isResubscription && (
        <Box sx={{ mt: 3 }}>
          <Alert severity="info">
            <Typography variant="body2">
              <strong>Upgrade:</strong> Changes apply immediately with prorated billing.<br />
              <strong>Downgrade:</strong> Changes apply at the end of your current billing period.
            </Typography>
          </Alert>
        </Box>
      )}

      {isResubscription && (
        <Box sx={{ mt: 3 }}>
          <Alert severity="success">
            <Typography variant="body2">
              <strong>Resubscription:</strong> Your subscription will activate immediately after payment, 
              and all your data will be restored within seconds. No setup required!
            </Typography>
          </Alert>
        </Box>
      )}
    </Box>
  );
};

export default PlanComparison;